import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { 
  Play, Check, Crown, ShoppingBag, ArrowRight
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import VODFooter from '../components/VODFooter';

const API = process.env.REACT_APP_BACKEND_URL || '';

const VODPaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [purchaseType, setPurchaseType] = useState(null);

  useEffect(() => {
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    const sessionId = searchParams.get('session_id');
    const videoId = searchParams.get('video_id');
    const tier = searchParams.get('tier');
    
    if (!sessionId) {
      setError('Invalid payment session');
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('livestreamToken');
    if (!token) {
      navigate('/live/login');
      return;
    }

    try {
      if (tier) {
        // Subscription verification
        setPurchaseType('subscription');
        await axios.post(
          `${API}/api/vod/payment/verify-subscription`,
          null,
          { 
            params: { session_id: sessionId, tier },
            headers: { Authorization: `Bearer ${token}` } 
          }
        );
      } else if (videoId) {
        // Video purchase verification
        setPurchaseType('video');
        await axios.post(
          `${API}/api/vod/payment/verify-purchase`,
          null,
          { 
            params: { session_id: sessionId, video_id: videoId },
            headers: { Authorization: `Bearer ${token}` } 
          }
        );
      }
      setSuccess(true);
      
      // Auto-redirect to video page after successful purchase (3 second delay)
      if (videoId) {
        setTimeout(() => {
          navigate(`/vod/video/${videoId}`);
        }, 3000);
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.response?.data?.detail || 'Failed to verify payment');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">!</span>
          </div>
          <h1 className="text-2xl font-bold mb-4">Payment Error</h1>
          <p className="text-gray-400 mb-8">{error}</p>
          <div className="flex gap-4 justify-center">
            <Button
              onClick={() => navigate('/vod')}
              variant="outline"
              className="border-white/20"
            >
              Back to VOD
            </Button>
            <Button
              onClick={() => window.location.reload()}
              className="bg-purple-600 hover:bg-purple-500"
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center text-white">
      <div className="text-center max-w-lg mx-auto px-4">
        {/* Success Animation */}
        <div className="relative mb-8">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-3xl" />
          <div className="relative w-24 h-24 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center mx-auto">
            <Check className="h-12 w-12 text-white" strokeWidth={3} />
          </div>
        </div>

        <h1 className="text-3xl font-bold mb-4">
          {purchaseType === 'subscription' ? 'Subscription Activated!' : 'Purchase Complete!'}
        </h1>
        
        <p className="text-gray-400 text-lg mb-8">
          {purchaseType === 'subscription' 
            ? 'Your subscription is now active. Enjoy unlimited access to our content library!'
            : 'Your video is ready! Redirecting you to watch now...'}
        </p>

        {/* Info Card */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-center gap-3 text-purple-400">
            {purchaseType === 'subscription' ? (
              <>
                <Crown className="h-6 w-6" />
                <span className="font-semibold">Subscription Active</span>
              </>
            ) : (
              <>
                <ShoppingBag className="h-6 w-6" />
                <span className="font-semibold">Video Purchased</span>
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {purchaseType === 'subscription' ? (
            <>
              <Button
                onClick={() => navigate('/vod')}
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
              >
                <Play className="mr-2 h-5 w-5" />
                Go to VOD
              </Button>
              <Button
                onClick={() => navigate('/vod/my-library')}
                size="lg"
                variant="outline"
                className="border-white/20"
              >
                Go to My Library
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  const videoId = searchParams.get('video_id');
                  if (videoId) navigate(`/vod/watch/${videoId}`);
                }}
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Now
              </Button>
              <Button
                onClick={() => navigate('/vod/my-library')}
                size="lg"
                variant="outline"
                className="border-white/20"
              >
                Go to My Library
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </>
          )}
        </div>
      </div>
      
      {/* Footer */}
      <VODFooter />
    </div>
  );
};

export default VODPaymentSuccess;
